import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true); setError("");
    try {
      await login(password);
      navigate("/", { replace: true });
    } catch {
      setError("Incorrect password. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "linear-gradient(135deg,#f0fdf4 0%,#ffffff 60%,#f0fdf4 100%)" }}>
      <div className="w-full max-w-sm">

        {/* Logo + title */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.jpg" alt="XSTRONG POWER" className="h-20 w-auto object-contain mb-4 drop-shadow-sm" />
          <h1 className="text-xl font-bold text-slate-900 text-center" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            XSTRONG POWER EV CHARGER
          </h1>
          <p className="text-sm text-slate-500 mt-1">Service Center — Staff Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center">
              <Lock size={15} className="text-white" />
            </div>
            <h2 className="text-base font-semibold text-slate-900">Staff Login</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPwd ? "text" : "password"}
                  placeholder="Enter staff password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={`h-10 pr-10 ${error ? "border-rose-400" : ""}`}
                  autoFocus
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {error && (
                <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-rose-500 inline-block" />{error}
                </p>
              )}
            </div>

            <Button type="submit"
              className="w-full bg-green-600 hover:bg-green-700 text-white h-10 font-medium"
              disabled={loading || !password}
              data-testid="login-submit-button">
              {loading
                ? <><Loader2 size={15} className="mr-2 animate-spin" />Signing in...</>
                : <><Lock size={15} className="mr-2" />Sign In to Staff Portal</>}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          XSTRONG POWER EV CHARGER &mdash; Service Center v1.0
        </p>
      </div>
    </div>
  );
}
